import React, { useState, useEffect, useMemo } from 'react';
import { subjectService, reportCardService } from '../../../services/index.js';
import { Trash2, Plus, Calendar, X, ChevronDown, ChevronUp, Search, Edit2, BookOpen, Clock, FileText, Award, User, Check, Ticket } from 'lucide-react';

const STANDARD_SCHOOL_CLASSES = ['LKG', 'UKG', 'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10'];
const COMMON_SCHOOL_SUBJECTS = ['KANNADA', 'ENGLISH', 'HINDI', 'MATHEMATICS', 'SCIENCE', 'SOCIAL SCIENCE', 'EVS', 'GENERAL KNOWLEDGE', 'COMPUTER SCIENCE'];

const DEFAULT_EXAM_PRESETS = [
  { examCode: 'FA-1', examName: 'Formative Assessment 1', examCategory: 'FORMATIVE', defaultMaxMarks: 20, termLabel: 'Term 1' },
  { examCode: 'FA-2', examName: 'Formative Assessment 2', examCategory: 'FORMATIVE', defaultMaxMarks: 20, termLabel: 'Term 1' },
  { examCode: 'SA-1', examName: 'Summative Assessment 1', examCategory: 'SUMMATIVE', defaultMaxMarks: 80, termLabel: 'Term 1' },
  { examCode: 'FA-3', examName: 'Formative Assessment 3', examCategory: 'FORMATIVE', defaultMaxMarks: 20, termLabel: 'Term 2' },
  { examCode: 'FA-4', examName: 'Formative Assessment 4', examCategory: 'FORMATIVE', defaultMaxMarks: 20, termLabel: 'Term 2' },
  { examCode: 'SA-2', examName: 'Summative Assessment 2', examCategory: 'SUMMATIVE', defaultMaxMarks: 80, termLabel: 'Term 2' }
];

const cleanStr = (s) => (s || '').toString().toLowerCase().trim();
const cleanSec = (s) => cleanStr(s).replace(/^section\s*/i, '').trim();

const normalizeClassName = (name) => {
  if (!name) return '';
  return name
    .toString()
    .toLowerCase()
    .replace(/^class\s*/i, '')
    .replace(/\s*standard$/i, '')
    .replace(/\s*std$/i, '')
    .replace(/(st|nd|rd|th)$/i, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();
};

const matchClassNames = (classA, classB) => {
  if (!classA && !classB) return true;
  if (!classA || !classB) return false;
  const a = cleanStr(classA);
  const b = cleanStr(classB);
  if (a === b) return true;
  const normA = normalizeClassName(a);
  const normB = normalizeClassName(b);
  return normA.length > 0 && normA === normB;
};

const ExamSetup = ({
  orgData,
  classes = [],
  teachers = [],
  tests = [],
  loadTests,
  activeAcademicYear,
  availableAcademicYears = [],
  examTypeConfigs = [],
  onOpenHallTickets,
  editPreselect = null,
  onClearPreselect,
  showSuccessNotification,
  showErrorNotification
}) => {
  // Scopes & Filter States
  const resolveInitialYear = () => {
    if (activeAcademicYear) return activeAcademicYear;
    if (orgData?.academicYear) return orgData.academicYear;
    if (orgData?.activeAcademicYear) return orgData.activeAcademicYear;
    const stored = localStorage.getItem('academicYear') || localStorage.getItem('activeAcademicYear');
    if (stored) return stored;
    const d = new Date();
    const y = d.getFullYear();
    const start = d.getMonth() >= 4 ? y : y - 1;
    return `${start}-${(start + 1).toString().slice(-2)}`;
  };

  const [academicYear, setAcademicYear] = useState(resolveInitialYear);
  const [activeSubTab, setActiveSubTab] = useState('OFFICIAL_EXAMS'); // 'OFFICIAL_EXAMS' | 'TEACHER_TESTS'
  const [classFilter, setClassFilter] = useState('ALL');
  const [sectionFilter, setSectionFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedExamKey, setExpandedExamKey] = useState(null);

  // Modal State for Announce / Edit Exam
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExamGroup, setEditingExamGroup] = useState(null);
  const [selectedExamTypeOption, setSelectedExamTypeOption] = useState(examTypeConfigs?.[0]?.examCode || 'FA-1');
  const [customExamTitle, setCustomExamTitle] = useState('');
  const [modalClass, setModalClass] = useState(() => (classes && classes.length > 0 ? (classes[0].className || classes[0].name) : ''));
  const [modalSection, setModalSection] = useState('ALL');
  const [testDate, setTestDate] = useState(new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [maxMarks, setMaxMarks] = useState(20);
  const [baseMaxMarks, setBaseMaxMarks] = useState(40);
  const [componentType, setComponentType] = useState('NONE'); // 'NONE' | 'ORAL' | 'INTERNAL'
  const [componentMaxMarks, setComponentMaxMarks] = useState(10);

  const totalCalculatedMax = useMemo(() => {
    const base = parseInt(baseMaxMarks, 10) || 0;
    if (componentType === 'NONE') return base;
    const comp = parseInt(componentMaxMarks, 10) || 0;
    return base + comp;
  }, [baseMaxMarks, componentType, componentMaxMarks]);
  const [allocatedSubjects, setAllocatedSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [customSubjectInput, setCustomSubjectInput] = useState('');
  const [customSubjectMarks, setCustomSubjectMarks] = useState({});
  const [customSubjectDates, setCustomSubjectDates] = useState({});
  const [customSubjectStartTimes, setCustomSubjectStartTimes] = useState({});
  const [customSubjectEndTimes, setCustomSubjectEndTimes] = useState({});
  const [editingMarksMap, setEditingMarksMap] = useState({});
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [applyToOtherClasses, setApplyToOtherClasses] = useState(false);

  useEffect(() => {
    if (activeAcademicYear) {
      setAcademicYear(activeAcademicYear);
    }
  }, [activeAcademicYear]);

  // Dynamic academic year list
  const academicYearOptions = useMemo(() => {
    if (availableAcademicYears && availableAcademicYears.length > 0) {
      return availableAcademicYears;
    }
    const years = new Set();
    if (academicYear) years.add(academicYear);
    const curYear = new Date().getFullYear();
    for (let i = -2; i <= 2; i++) {
      const y = curYear + i;
      years.add(`${y}-${(y + 1).toString().slice(-2)}`);
    }
    return Array.from(years).sort().reverse();
  }, [availableAcademicYears, academicYear]);

  const availableClasses = useMemo(() => {
    return classes && classes.length > 0
      ? [...new Set(classes.map(c => c.className || c.name))].filter(Boolean)
      : STANDARD_SCHOOL_CLASSES;
  }, [classes]);

  // Synchronize modalClass when availableClasses is loaded
  useEffect(() => {
    if (availableClasses.length > 0 && !availableClasses.includes(modalClass)) {
      setModalClass(availableClasses[0]);
    }
  }, [availableClasses, modalClass]);

  /**
   * DB-driven sections for class filter and modal.
   * Derived from classes prop (loaded from classes_tbl).
   * Never hardcodes A/B/C/D.
   */
  const getSectionsForClass = (clsName) => {
    if (!classes || classes.length === 0 || !clsName || clsName === 'ALL') return [];
    const normalized = (s) => (s || '').toString().toLowerCase().trim();
    const relevant = classes.filter(c => normalized(c.className || c.name) === normalized(clsName));
    const unique = [...new Set(relevant.map(c => c.section || c.sectionName).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return unique;
  };

  const filterSectionOptions = useMemo(() => {
    return getSectionsForClass(classFilter);
  }, [classes, classFilter]);

  const modalSectionOptions = useMemo(() => {
    return getSectionsForClass(modalClass);
  }, [classes, modalClass]);

  // Combined exam types options for dropdown (config > presets)
  const availableExamTypes = useMemo(() => {
    if (examTypeConfigs && examTypeConfigs.length > 0) {
      return examTypeConfigs;
    }
    return DEFAULT_EXAM_PRESETS;
  }, [examTypeConfigs]);

  // Set default modal selection when availableExamTypes load
  useEffect(() => {
    if (availableExamTypes.length > 0 && (!selectedExamTypeOption || selectedExamTypeOption === 'FA-1')) {
      const first = availableExamTypes[0];
      setSelectedExamTypeOption(first.examCode);
      const def = first.defaultMaxMarks || 40;
      setBaseMaxMarks(def);
      setMaxMarks(def);
    }
  }, [availableExamTypes]);

  // When admin selects an exam type from dropdown, pre-populate default max marks
  const handleExamTypeSelect = (e) => {
    const val = e.target.value;
    setSelectedExamTypeOption(val);
    if (val !== '__CUSTOM__') {
      const found = availableExamTypes.find(et => et.examCode === val);
      if (found && found.defaultMaxMarks) {
        setBaseMaxMarks(found.defaultMaxMarks);
        setMaxMarks(found.defaultMaxMarks);
      }
    }
  };

  // Load subject allocations when modal opens or modal class changes
  useEffect(() => {
    if (isModalOpen && modalClass) {
      fetchAllocations(modalClass, modalSection);
    }
  }, [isModalOpen, modalClass, modalSection, academicYear]);

  const normalizeAcademicYear = (year) => {
    if (!year) return year;
    const match = year.match(/^(\d{4})-(\d{2})$/);
    if (match) {
      const century = match[1].slice(0, 2);
      return `${match[1]}-${century}${match[2]}`;
    }
    return year;
  };

  const fetchAllocations = async (cls, sec) => {
    setLoadingSubjects(true);
    try {
      const normalizedYear = normalizeAcademicYear(academicYear);
      let orgId = orgData?.orgId || localStorage.getItem('orgId');
      if (!orgId) {
        try {
          const uStr = localStorage.getItem('user');
          if (uStr) {
            const u = JSON.parse(uStr);
            orgId = u.orgId || u.organizationId;
          }
        } catch (e) {}
      }
      const allAllocations = await subjectService.getOrgSubjectAllocations(orgId, normalizedYear);
      let filtered = Array.isArray(allAllocations) ? allAllocations : [];
      const cleanSec = (s) => (s || '').toString().toLowerCase().replace(/^section\s*/i, '').trim();
      const cleanCls = (s) => (s || '').toString().toLowerCase().trim();

      if (cls) {
        filtered = filtered.filter(a => a.className && cleanCls(a.className) === cleanCls(cls));
      }
      if (sec && sec !== 'ALL') {
        filtered = filtered.filter(a => {
          if (!a.sectionName) return true;
          const s = cleanSec(a.sectionName);
          return s === 'all' || s === '' || s === cleanSec(sec);
        });
      }
      setAllocatedSubjects(filtered);
      if (!editingExamGroup) {
        if (filtered.length > 0) {
          const names = [...new Set(filtered.map(a => a.subjectName).filter(Boolean))];
          setSelectedSubjects(names);
        } else {
          setSelectedSubjects([...COMMON_SCHOOL_SUBJECTS.slice(0, 5)]);
        }
      }
    } catch (err) {
      console.error('Failed to load subject allocations:', err);
      setAllocatedSubjects([]);
      if (!editingExamGroup) {
        setSelectedSubjects([...COMMON_SCHOOL_SUBJECTS.slice(0, 5)]);
      }
    } finally {
      setLoadingSubjects(false);
    }
  };

  const handleToggleSubject = (subjName) => {
    if (selectedSubjects.includes(subjName)) {
      setSelectedSubjects(selectedSubjects.filter(s => s !== subjName));
    } else {
      setSelectedSubjects([...selectedSubjects, subjName]);
      if (customSubjectMarks[subjName] === undefined) {
        setCustomSubjectMarks(prev => ({ ...prev, [subjName]: maxMarks }));
      }
    }
  };

  const handleSubjectMarkChange = (subjName, markVal) => {
    setCustomSubjectMarks(prev => ({
      ...prev,
      [subjName]: markVal
    }));
  };

  const handleSelectAllSubjects = () => {
    let pool = [];
    if (allocatedSubjects.length > 0) {
      pool = [...new Set(allocatedSubjects.map(a => a.subjectName).filter(Boolean))];
    } else {
      pool = [...COMMON_SCHOOL_SUBJECTS];
    }
    setSelectedSubjects(pool);
  };

  const handleClearAllSubjects = () => {
    setSelectedSubjects([]);
  };

  const handleAddCustomSubject = (e) => {
    e.preventDefault();
    const clean = customSubjectInput.trim().toUpperCase();
    if (!clean) return;
    if (!selectedSubjects.includes(clean)) {
      setSelectedSubjects([...selectedSubjects, clean]);
      setCustomSubjectMarks(prev => ({ ...prev, [clean]: maxMarks }));
    }
    setCustomSubjectInput('');
  };

  const getTeacherName = (teacherId) => {
    if (!teacherId || teacherId === 'ADMIN') return 'School Admin';
    const found = teachers.find(t => t.teacherId === teacherId || t.id?.toString() === teacherId);
    return found ? (found.teacherName || found.name || `Teacher (${teacherId})`) : `Teacher (${teacherId})`;
  };

  const handleOpenAnnounceExam = () => {
    setEditingExamGroup(null);
    let defMarks = 40;
    if (availableExamTypes.length > 0) {
      setSelectedExamTypeOption(availableExamTypes[0].examCode);
      defMarks = availableExamTypes[0].defaultMaxMarks || 40;
    }
    setBaseMaxMarks(defMarks);
    setComponentType('NONE');
    setComponentMaxMarks(10);
    setMaxMarks(defMarks);
    setCustomExamTitle('');
    if (availableClasses.length > 0) {
      setModalClass(availableClasses[0]);
    }
    setModalSection('ALL');
    setTestDate(new Date().toISOString().slice(0, 10));
    setStartTime('');
    setEndTime('');
    setSelectedSubjects([]);
    setCustomSubjectMarks({});
    setCustomSubjectDates({});
    setCustomSubjectStartTimes({});
    setCustomSubjectEndTimes({});
    setEditingMarksMap({});
    setIsModalOpen(true);
  };

  const handleOpenEditExam = (exam) => {
    setEditingExamGroup(exam);
    const isPreset = availableExamTypes.some(et => et.examCode === exam.title);
    if (isPreset) {
      setSelectedExamTypeOption(exam.title);
      setCustomExamTitle('');
    } else {
      setSelectedExamTypeOption('__CUSTOM__');
      setCustomExamTitle(exam.title);
    }
    setModalClass(exam.className);
    setModalSection(exam.section || 'ALL');
    setTestDate(exam.testDate || new Date().toISOString().slice(0, 10));
    const firstWithStart = exam.subjects?.find(s => s.startTime);
    const firstWithEnd = exam.subjects?.find(s => s.endTime);
    setStartTime(firstWithStart?.startTime || '');
    setEndTime(firstWithEnd?.endTime || '');

    const firstSubj = exam.subjects?.[0];
    const cType = exam.componentType || firstSubj?.componentType || 'NONE';
    const cMax = exam.componentMaxMarks !== undefined ? exam.componentMaxMarks : (firstSubj?.componentMaxMarks !== undefined ? firstSubj.componentMaxMarks : 10);
    const bMax = exam.baseMaxMarks || firstSubj?.baseMaxMarks || (cType !== 'NONE' ? ((exam.maxMarks || 50) - cMax) : (exam.maxMarks || 40));

    setComponentType(cType);
    setComponentMaxMarks(cMax);
    setBaseMaxMarks(bMax);
    setMaxMarks(exam.maxMarks || (bMax + (cType !== 'NONE' ? cMax : 0)));
    setSelectedSubjects(exam.subjects.map(s => s.subjectName));

    const marksMap = {};
    const datesMap = {};
    const startTimesMap = {};
    const endTimesMap = {};
    exam.subjects.forEach(s => {
      const sBase = s.baseMaxMarks !== undefined ? s.baseMaxMarks : (s.maxMarks !== undefined && cType !== 'NONE' && cMax > 0 && s.maxMarks > cMax ? (s.maxMarks - cMax) : (s.maxMarks !== undefined ? s.maxMarks : bMax));
      if (sBase !== bMax) {
        marksMap[s.subjectName] = sBase;
      }
      datesMap[s.subjectName] = s.testDate || exam.testDate || new Date().toISOString().slice(0, 10);
      if (s.startTime) startTimesMap[s.subjectName] = s.startTime;
      if (s.endTime) endTimesMap[s.subjectName] = s.endTime;
    });
    setCustomSubjectMarks(marksMap);
    setCustomSubjectDates(datesMap);
    setCustomSubjectStartTimes(startTimesMap);
    setCustomSubjectEndTimes(endTimesMap);
    setEditingMarksMap({});
    setApplyToOtherClasses(false);
    setIsModalOpen(true);
  };

  // Helper: auto-stagger dates consecutively across subjects starting from base date
  const handleAutoStaggerDates = () => {
    if (selectedSubjects.length === 0) return;
    const baseDateStr = customSubjectDates[selectedSubjects[0]] || testDate || new Date().toISOString().slice(0, 10);
    const parts = baseDateStr.split('-');
    if (parts.length !== 3) return;

    let cur = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    const newDates = { ...customSubjectDates };

    selectedSubjects.forEach((subj, idx) => {
      if (idx > 0) {
        cur.setDate(cur.getDate() + 1);
        // Skip Sunday
        if (cur.getDay() === 0) {
          cur.setDate(cur.getDate() + 1);
        }
      }
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, '0');
      const d = String(cur.getDate()).padStart(2, '0');
      newDates[subj] = `${y}-${m}-${d}`;
    });
    setCustomSubjectDates(newDates);
  };

  // Helper: apply start and end time from first subject to all selected subjects
  const handleApplyTimeToAll = () => {
    if (selectedSubjects.length === 0) return;
    const firstSubj = selectedSubjects[0];
    const sourceStart = customSubjectStartTimes[firstSubj] || startTime;
    const sourceEnd = customSubjectEndTimes[firstSubj] || endTime;

    const newStarts = { ...customSubjectStartTimes };
    const newEnds = { ...customSubjectEndTimes };
    selectedSubjects.forEach(s => {
      if (sourceStart) newStarts[s] = sourceStart;
      if (sourceEnd) newEnds[s] = sourceEnd;
    });
    setCustomSubjectStartTimes(newStarts);
    setCustomSubjectEndTimes(newEnds);
  };

  // Announce or Edit School Exam
  const handleSaveExam = async (e) => {
    e.preventDefault();
    const finalExamTitle = selectedExamTypeOption === '__CUSTOM__' ? customExamTitle.trim() : selectedExamTypeOption.trim();
    if (!finalExamTitle) {
      alert('Please select or enter an Exam Title.');
      return;
    }
    if (selectedSubjects.length === 0) {
      alert('Please select at least one subject to include in this exam.');
      return;
    }

    setSubmitting(true);
    try {
      const matchedConfig = availableExamTypes.find(et => et.examCode === finalExamTitle);
      const termLabel = matchedConfig?.termLabel || (finalExamTitle.includes('SA-2') || finalExamTitle.includes('Final') ? 'Term 2' : 'Term 1');

      // Detect matching existing exam group (either currently editing or already in list)
      const targetExamGroup = editingExamGroup || schoolExams.find(g =>
        cleanStr(g.title) === cleanStr(finalExamTitle) &&
        matchClassNames(g.className, modalClass) &&
        cleanSec(g.section) === cleanSec(modalSection || 'ALL')
      );

      const existingSubjectsMap = new Map();
      if (targetExamGroup && Array.isArray(targetExamGroup.subjects)) {
        targetExamGroup.subjects.forEach(s => {
          if (s.subjectName) existingSubjectsMap.set(s.subjectName.trim().toUpperCase(), s);
        });
      }

      let savedCount = 0;
      const errors = [];

      for (const subjName of selectedSubjects) {
        const normSubj = subjName.trim().toUpperCase();
        const allocation = allocatedSubjects.find(a => cleanStr(a.subjectName) === cleanStr(subjName));
        const rawSubjMark = customSubjectMarks[subjName];
        const subjBase = (rawSubjMark !== undefined && rawSubjMark !== '' && !isNaN(parseInt(rawSubjMark, 10)))
          ? parseInt(rawSubjMark, 10)
          : parseInt(baseMaxMarks, 10);
        const cType = componentType || 'NONE';
        const cMax = cType !== 'NONE' ? (parseInt(componentMaxMarks, 10) || 0) : 0;
        const subjTotal = subjBase + cMax;

        const subjDate = customSubjectDates[subjName] || testDate;
        const subjStartTime = customSubjectStartTimes[subjName] || startTime || null;
        const subjEndTime = customSubjectEndTimes[subjName] || endTime || null;

        const payload = {
          title: finalExamTitle,
          description: academicYear,
          className: modalClass,
          section: modalSection || 'ALL',
          semester: termLabel,
          subjectName: subjName,
          subjectId: allocation ? allocation.subjectId : -1,
          testDate: subjDate,
          startTime: subjStartTime,
          endTime: subjEndTime,
          baseMaxMarks: subjBase,
          componentType: cType,
          componentMaxMarks: cMax,
          maxMarks: subjTotal,
          testType: 'EXAM',
          instructions: 'Official School Examination',
          teacherId: (allocation && allocation.teacherId) ? String(allocation.teacherId) : 'ADMIN',
          adminEmail: orgData?.email || 'admin@school.edu'
        };

        try {
          if (existingSubjectsMap.has(normSubj)) {
            const existing = existingSubjectsMap.get(normSubj);
            const res = await reportCardService.updateTest(existing.testId, payload);
            if (res && (res.success || res.test || res.testId)) {
              savedCount++;
            }
          } else {
            const res = await reportCardService.createTest(payload);
            if (res && (res.success || res.test || res.testId)) {
              savedCount++;
            }
          }
        } catch (subErr) {
          console.error(`Failed to save subject ${subjName}:`, subErr);
          errors.push(`${subjName}: ${subErr.message}`);
        }
      }

      // If user was explicitly in "Edit Timetable" mode, delete any subjects they removed
      if (editingExamGroup) {
        for (const [oldSubjNorm, oldTest] of existingSubjectsMap.entries()) {
          const stillSelected = selectedSubjects.some(s => s.trim().toUpperCase() === oldSubjNorm);
          if (!stillSelected && oldTest.testId) {
            try {
              await reportCardService.deleteTest(oldTest.testId);
            } catch (delErr) {
              console.warn(`Failed to delete deselected subject ${oldTest.subjectName}:`, delErr);
            }
          }
        }
      }

      // Propagate marks & components to other classes having this exam title if requested
      if (applyToOtherClasses && finalExamTitle) {
        const otherExams = schoolExams.filter(g =>
          cleanStr(g.title) === cleanStr(finalExamTitle) &&
          !matchClassNames(g.className, modalClass)
        );
        for (const grp of otherExams) {
          if (Array.isArray(grp.subjects)) {
            for (const ot of grp.subjects) {
              if (ot.testId) {
                try {
                  const otSubjBase = parseInt(baseMaxMarks, 10) || 20;
                  const otCType = componentType || 'NONE';
                  const otCMax = otCType !== 'NONE' ? (parseInt(componentMaxMarks, 10) || 0) : 0;
                  await reportCardService.updateTest(ot.testId, {
                    title: ot.title,
                    baseMaxMarks: otSubjBase,
                    componentType: otCType,
                    componentMaxMarks: otCMax,
                    maxMarks: otSubjBase + otCMax
                  });
                } catch (otErr) {
                  console.warn(`Failed to update test for class ${grp.className}:`, otErr);
                }
              }
            }
          }
        }
      }

      if (savedCount > 0) {
        showSuccessNotification(
          targetExamGroup ? 'Exam Saved' : 'Exam Announced',
          `Successfully saved "${finalExamTitle}" with ${savedCount} subject(s) for ${modalClass}${modalSection !== 'ALL' ? ` (Sec ${modalSection})` : ''}.${applyToOtherClasses ? ' Also updated marks across other classes.' : ''}`
        );
        if (typeof loadTests === 'function') await loadTests();
        setIsModalOpen(false);
        setEditingExamGroup(null);
      } else {
        showErrorNotification('Failed', errors.length > 0 ? errors.join('; ') : 'Failed to schedule exam subjects.');
      }
    } catch (err) {
      showErrorNotification('Error', err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete an entire announced school exam (all subjects)
  const handleDeleteExamGroup = async (examGroup) => {
    if (!window.confirm(`Are you sure you want to remove "${examGroup.title}" for ${examGroup.className}${examGroup.section !== 'ALL' ? ` (Sec ${examGroup.section})` : ''}? This will remove all ${examGroup.subjects.length} scheduled subject(s).`)) {
      return;
    }

    try {
      let removedCount = 0;
      for (const sub of examGroup.subjects) {
        if (sub.testId) {
          const res = await reportCardService.deleteTest(sub.testId);
          if (res && res.success) removedCount++;
        }
      }
      showSuccessNotification('Exam Removed', `Removed ${removedCount} subject(s) for "${examGroup.title}".`);
      if (typeof loadTests === 'function') loadTests();
    } catch (err) {
      showErrorNotification('Error', err.message);
    }
  };

  // Delete a single subject from an exam
  const handleDeleteSingleSubject = async (testId, subjectName) => {
    if (!window.confirm(`Remove subject "${subjectName}" from this exam?`)) return;
    try {
      const res = await reportCardService.deleteTest(testId);
      if (res && res.success) {
        showSuccessNotification('Deleted', `Subject "${subjectName}" removed.`);
        if (typeof loadTests === 'function') loadTests();
      } else {
        showErrorNotification('Failed', res.error || 'Failed to remove subject.');
      }
    } catch (err) {
      showErrorNotification('Error', err.message);
    }
  };

  // Delete a teacher-assigned test
  const handleDeleteTeacherTest = async (testId, title) => {
    if (!window.confirm(`Delete teacher test "${title}"?`)) return;
    try {
      const res = await reportCardService.deleteTest(testId);
      if (res && res.success) {
        showSuccessNotification('Deleted', `Teacher test "${title}" removed.`);
        if (typeof loadTests === 'function') loadTests();
      } else {
        showErrorNotification('Failed', res.error || 'Failed to delete test.');
      }
    } catch (err) {
      showErrorNotification('Error', err.message);
    }
  };

  /**
   * CRITICAL SEPARATION RULE:
   * Official School Exams vs Teacher Classroom Tests
   */
  const isOfficialSchoolExam = (t) => {
    if (t.createdByRole === 'TEACHER' && t.teacherId && t.teacherId !== 'ADMIN' && t.testType !== 'EXAM' && t.testType !== 'SCHOOL_EXAM') {
      return false; // Classroom test created by teacher -> belongs in Teacher Module / Teacher Tests tab
    }
    return true;
  };

  const isTeacherAssignedTest = (t) => {
    return !isOfficialSchoolExam(t);
  };

  // Group official school exams by title + className + section
  const schoolExams = useMemo(() => {
    const groups = {};
    const normActiveYear = normalizeAcademicYear(academicYear);

    tests.filter(isOfficialSchoolExam).forEach(t => {
      // Academic year matching
      const tYear = (t.description || t.academicYear || '').trim();
      if (tYear && /^\d{4}-\d{2,4}$/.test(tYear)) {
        if (normalizeAcademicYear(tYear) !== normActiveYear) return;
      }

      const key = `${t.title || 'Exam'} - ${t.className || 'Class'} - ${t.section || 'ALL'}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          title: t.title || 'Exam',
          className: t.className || 'Class',
          section: t.section || 'ALL',
          semester: t.semester || 'Term 1',
          testDate: t.testDate,
          maxMarks: t.maxMarks || 20,
          baseMaxMarks: t.baseMaxMarks,
          componentType: t.componentType || 'NONE',
          componentMaxMarks: t.componentMaxMarks || 0,
          status: t.status || 'SCHEDULED',
          subjects: []
        };
      }
      groups[key].subjects.push(t);
      if (t.componentType && t.componentType !== 'NONE') {
        groups[key].componentType = t.componentType;
        groups[key].componentMaxMarks = t.componentMaxMarks;
        groups[key].baseMaxMarks = t.baseMaxMarks;
      }
    });

    Object.values(groups).forEach(g => {
      const marksList = g.subjects.map(s => s.maxMarks || 20);
      g.minMarks = Math.min(...marksList);
      g.maxMarks = Math.max(...marksList);
    });

    let list = Object.values(groups);

    // Apply Filters: Class, Section, Status, Search
    if (classFilter !== 'ALL') {
      list = list.filter(e => (e.className || '').toLowerCase() === classFilter.toLowerCase());
    }
    if (sectionFilter !== 'ALL') {
      list = list.filter(e => (e.section || 'ALL').toLowerCase() === sectionFilter.toLowerCase());
    }
    if (statusFilter !== 'ALL') {
      list = list.filter(e => e.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(e =>
        (e.title || '').toLowerCase().includes(q) ||
        (e.className || '').toLowerCase().includes(q) ||
        (e.subjects && e.subjects.some(s => (s.subjectName || '').toLowerCase().includes(q)))
      );
    }

    // Sort by examTypeConfig order when available
    if (availableExamTypes && availableExamTypes.length > 0) {
      const orderMap = {};
      availableExamTypes.forEach((et, idx) => { orderMap[et.examCode] = et.sortOrder || idx + 1; });
      list.sort((a, b) => {
        const oa = orderMap[a.title] !== undefined ? orderMap[a.title] : 9999;
        const ob = orderMap[b.title] !== undefined ? orderMap[b.title] : 9999;
        if (oa !== ob) return oa - ob;
        return a.className.localeCompare(b.className);
      });
    }

    return list;
  }, [tests, academicYear, classFilter, sectionFilter, statusFilter, searchQuery, availableExamTypes]);

  // Auto-open edit modal if navigated with editPreselect
  useEffect(() => {
    if (editPreselect && schoolExams.length > 0) {
      const target = schoolExams.find(e =>
        e.title === editPreselect.examTitle &&
        (e.className || '').toLowerCase() === (editPreselect.className || '').toLowerCase() &&
        (!editPreselect.section || editPreselect.section === 'ALL' || (e.section || 'ALL').toLowerCase() === (editPreselect.section || '').toLowerCase())
      ) || schoolExams.find(e =>
        e.title === editPreselect.examTitle &&
        (e.className || '').toLowerCase() === (editPreselect.className || '').toLowerCase()
      );

      if (target) {
        handleOpenEditExam(target);
        if (typeof onClearPreselect === 'function') onClearPreselect();
      }
    }
  }, [editPreselect, schoolExams]);

  // List of tests assigned by teachers for this academic year
  const teacherTests = useMemo(() => {
    const normActiveYear = normalizeAcademicYear(academicYear);

    let list = tests.filter(isTeacherAssignedTest).filter(t => {
      const tYear = (t.description || t.academicYear || '').trim();
      if (tYear && /^\d{4}-\d{2,4}$/.test(tYear)) {
        if (normalizeAcademicYear(tYear) !== normActiveYear) return false;
      }
      return true;
    });

    // Apply Filters: Class, Section, Status, Search
    if (classFilter !== 'ALL') {
      list = list.filter(t => (t.className || '').toLowerCase() === classFilter.toLowerCase());
    }
    if (sectionFilter !== 'ALL') {
      list = list.filter(t => (t.section || 'ALL').toLowerCase() === sectionFilter.toLowerCase());
    }
    if (statusFilter !== 'ALL') {
      list = list.filter(t => t.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(t =>
        (t.title || '').toLowerCase().includes(q) ||
        (t.className || '').toLowerCase().includes(q) ||
        (t.subjectName || '').toLowerCase().includes(q) ||
        (t.teacherId || '').toLowerCase().includes(q)
      );
    }

    list.sort((a, b) => (b.testDate || '').localeCompare(a.testDate || ''));
    return list;
  }, [tests, academicYear, classFilter, sectionFilter, statusFilter, searchQuery]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      
      {/* 1. Integrated Control & Filter Card */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '14px 20px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px'
      }}>
        {/* Top Row: Sub-Tabs + Announce Exam Action */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          {/* Segmented Sub-Tab Switcher */}
          <div style={{
            display: 'flex',
            gap: '4px',
            background: '#f1f5f9',
            padding: '4px',
            borderRadius: '10px',
            border: '1px solid #e2e8f0'
          }}>
            <button
              type="button"
              onClick={() => setActiveSubTab('OFFICIAL_EXAMS')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 18px',
                borderRadius: '7px',
                fontSize: '13px',
                fontWeight: '700',
                border: 'none',
                cursor: 'pointer',
                background: activeSubTab === 'OFFICIAL_EXAMS' ? '#ffffff' : 'transparent',
                color: activeSubTab === 'OFFICIAL_EXAMS' ? '#1e3a8a' : '#64748b',
                boxShadow: activeSubTab === 'OFFICIAL_EXAMS' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s ease'
              }}
            >
              <Award size={15} color={activeSubTab === 'OFFICIAL_EXAMS' ? '#1e3a8a' : '#94a3b8'} />
              <span>Official School Exams</span>
              <span style={{
                background: activeSubTab === 'OFFICIAL_EXAMS' ? '#eff6ff' : '#e2e8f0',
                color: activeSubTab === 'OFFICIAL_EXAMS' ? '#1e3a8a' : '#64748b',
                padding: '2px 7px',
                borderRadius: '10px',
                fontSize: '11.5px',
                fontWeight: '800'
              }}>
                {schoolExams.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveSubTab('TEACHER_TESTS')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 18px',
                borderRadius: '7px',
                fontSize: '13px',
                fontWeight: '700',
                border: 'none',
                cursor: 'pointer',
                background: activeSubTab === 'TEACHER_TESTS' ? '#ffffff' : 'transparent',
                color: activeSubTab === 'TEACHER_TESTS' ? '#0f766e' : '#64748b',
                boxShadow: activeSubTab === 'TEACHER_TESTS' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s ease'
              }}
            >
              <BookOpen size={15} color={activeSubTab === 'TEACHER_TESTS' ? '#0f766e' : '#94a3b8'} />
              <span>Teacher Assigned Tests</span>
              <span style={{
                background: activeSubTab === 'TEACHER_TESTS' ? '#f0fdfa' : '#e2e8f0',
                color: activeSubTab === 'TEACHER_TESTS' ? '#0f766e' : '#64748b',
                padding: '2px 7px',
                borderRadius: '10px',
                fontSize: '11.5px',
                fontWeight: '800'
              }}>
                {teacherTests.length}
              </span>
            </button>
          </div>

          {/* Action on the right */}
          {activeSubTab === 'OFFICIAL_EXAMS' ? (
            <button
              type="button"
              onClick={handleOpenAnnounceExam}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#1e3a8a',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '9px 18px',
                fontSize: '13.5px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(30,58,138,0.25)',
                transition: 'all 0.15s ease'
              }}
            >
              <Plus size={16} /> Announce Exam
            </button>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              color: '#0f766e',
              background: '#f0fdfa',
              border: '1px solid #ccfbf1',
              padding: '6px 12px',
              borderRadius: '8px',
              fontWeight: '600'
            }}>
              <BookOpen size={14} /> Assigned directly by teachers via teacher portal
            </div>
          )}
        </div>

        {/* Bottom Row: Sleek Filters & Search */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          paddingTop: '12px',
          borderTop: '1px solid #f1f5f9'
        }}>
          {/* Academic Year */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b' }}>Year:</span>
            <select
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1.5px solid #cbd5e1',
                fontSize: '13px',
                fontWeight: '700',
                color: '#1e293b',
                background: '#ffffff',
                cursor: 'pointer'
              }}
            >
              {academicYearOptions.map(yr => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </div>

          {/* Class Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b' }}>Class:</span>
            <select
              value={classFilter}
              onChange={(e) => {
                setClassFilter(e.target.value);
                setSectionFilter('ALL');
              }}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1.5px solid #cbd5e1',
                fontSize: '13px',
                fontWeight: '700',
                color: '#1e293b',
                background: '#ffffff',
                cursor: 'pointer'
              }}
            >
              <option value="ALL">All Classes</option>
              {availableClasses.map(cls => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>

          {/* Section Filter */}
          {classFilter !== 'ALL' && filterSectionOptions.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b' }}>Section:</span>
              <select
                value={sectionFilter}
                onChange={(e) => setSectionFilter(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '13px',
                  fontWeight: '700',
                  color: '#1e293b',
                  background: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                <option value="ALL">All Sections</option>
                {filterSectionOptions.map(sec => (
                  <option key={sec} value={sec}>Section {sec}</option>
                ))}
              </select>
            </div>
          )}

          {/* Status Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b' }}>Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1.5px solid #cbd5e1',
                fontSize: '13px',
                fontWeight: '700',
                color: '#1e293b',
                background: '#ffffff',
                cursor: 'pointer'
              }}
            >
              <option value="ALL">All Status</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="COMPLETED">Completed</option>
              <option value="FROZEN">Locked</option>
            </select>
          </div>

          {/* Search Box */}
          <div style={{ position: 'relative', marginLeft: 'auto' }}>
            <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search title, class, subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '6px 12px 6px 30px',
                borderRadius: '6px',
                border: '1.5px solid #cbd5e1',
                fontSize: '13px',
                width: '210px',
                color: '#0f172a'
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: '2px',
                  display: 'flex'
                }}
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 2. Cards Grid based on activeSubTab */}
      {activeSubTab === 'OFFICIAL_EXAMS' ? (
        schoolExams.length === 0 ? (
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            padding: '56px 24px',
            textAlign: 'center',
            border: '1.5px dashed #cbd5e1'
          }}>
            <Award size={42} color="#94a3b8" style={{ marginBottom: '12px' }} />
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#334155' }}>
              {searchQuery ? `No exams matching "${searchQuery}"` : 'No school exams announced yet'}
            </h4>
            <p style={{ margin: '6px 0 18px 0', fontSize: '13px', color: '#64748b' }}>
              Click "Announce Exam" to schedule an official examination (e.g. FA-1, SA-1) for a class.
            </p>
            <button
              type="button"
              onClick={handleOpenAnnounceExam}
              style={{
                background: '#1e3a8a',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '9px 18px',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              + Announce Exam
            </button>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '16px'
          }}>
            {schoolExams.map((exam) => {
              const isExpanded = expandedExamKey === exam.key;
              const configMatch = availableExamTypes.find(et => et.examCode === exam.title);
              const categoryLabel = configMatch?.examName || configMatch?.examCategory || exam.semester || 'Assessment';
              const categoryColor = configMatch?.examCategory === 'SUMMATIVE' ? '#dc2626'
                : configMatch?.examCategory === 'FORMATIVE' ? '#2563eb'
                : configMatch?.examCategory === 'UNIT_TEST' ? '#d97706' : '#7c3aed';

              const statusColor = exam.status === 'FROZEN' ? '#475569' : exam.status === 'COMPLETED' ? '#16a34a' : '#1d4ed8';
              const statusBg = exam.status === 'FROZEN' ? '#f1f5f9' : exam.status === 'COMPLETED' ? '#f0fdf4' : '#eff6ff';

              return (
                <div
                  key={exam.key}
                  style={{
                    background: '#ffffff',
                    borderRadius: '14px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'box-shadow 0.15s ease'
                  }}
                >
                  {/* Card Header */}
                  <div style={{ padding: '16px 18px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                          border: '1px solid #bfdbfe',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <Award size={20} color="#1d4ed8" />
                        </div>
                        <div>
                          <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.2px' }}>
                            {exam.title}
                          </h4>
                          <div style={{ fontSize: '12px', color: categoryColor, fontWeight: '700', marginTop: '2px' }}>
                            {categoryLabel}
                          </div>
                        </div>
                      </div>

                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: '800',
                        background: statusBg,
                        color: statusColor,
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        flexShrink: 0
                      }}>
                        {exam.status === 'COMPLETED' ? '✓ Completed' : exam.status === 'FROZEN' ? '🔒 Locked' : '● Scheduled'}
                      </span>
                    </div>
                  </div>

                  {/* Card Body - Visual Grid */}
                  <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '8px'
                    }}>
                      <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Class & Sec</div>
                        <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {exam.className} {exam.section && exam.section !== 'ALL' ? `(Sec ${exam.section})` : '(All Sec)'}
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Exam Date</div>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#334155', marginTop: '2px' }}>
                          {exam.testDate || '—'}
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Subjects</div>
                        <div style={{ fontSize: '13px', fontWeight: '800', color: '#1d4ed8', marginTop: '2px' }}>
                          {exam.subjects.length} Subjects
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Max Marks</div>
                        <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a', marginTop: '2px' }}>
                          {exam.componentType === 'ORAL' ? (
                            <span style={{ color: '#0369a1' }}>
                              Written: {exam.baseMaxMarks || (exam.maxMarks - (exam.componentMaxMarks || 10))} + Oral: +{exam.componentMaxMarks || 10} = Total: {exam.maxMarks}m
                            </span>
                          ) : exam.componentType === 'INTERNAL' ? (
                            <span style={{ color: '#4338ca' }}>
                              Theory: {exam.baseMaxMarks || (exam.maxMarks - (exam.componentMaxMarks || 20))} + Internal: +{exam.componentMaxMarks || 20} = Total: {exam.maxMarks}m
                            </span>
                          ) : (
                            exam.minMarks === exam.maxMarks ? `${exam.maxMarks} Marks` : `${exam.minMarks} - ${exam.maxMarks} Marks`
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expandable Subject List */}
                    {isExpanded && (
                      <div style={{
                        marginTop: '4px',
                        paddingTop: '10px',
                        borderTop: '1px dashed #cbd5e1',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px'
                      }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>
                          Subject Timetable & Details:
                        </div>
                        {exam.subjects.map((sub, sIdx) => {
                          const timeStr = sub.startTime ? `${sub.startTime}${sub.endTime ? ` - ${sub.endTime}` : ''}` : '';
                          return (
                            <div
                              key={sub.testId || sIdx}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '7px 10px',
                                background: '#ffffff',
                                borderRadius: '6px',
                                border: '1px solid #e2e8f0',
                                fontSize: '12px',
                                gap: '8px',
                                flexWrap: 'wrap'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: '700', color: '#1e293b' }}>{sub.subjectName}</span>
                                <span style={{
                                  background: '#eff6ff',
                                  color: '#1d4ed8',
                                  border: '1px solid #dbeafe',
                                  borderRadius: '4px',
                                  padding: '1px 7px',
                                  fontSize: '11px',
                                  fontWeight: '600'
                                }}>
                                  📅 {sub.testDate || 'No Date'}
                                </span>
                                {timeStr ? (
                                  <span style={{
                                    background: '#f0fdf4',
                                    color: '#15803d',
                                    border: '1px solid #dcfce7',
                                    borderRadius: '4px',
                                    padding: '1px 7px',
                                    fontSize: '11px',
                                    fontWeight: '600'
                                  }}>
                                    ⏰ {timeStr}
                                  </span>
                                ) : (
                                  <span style={{
                                    background: '#fffbeb',
                                    color: '#b45309',
                                    border: '1px solid #fef3c7',
                                    borderRadius: '4px',
                                    padding: '1px 6px',
                                    fontSize: '10.5px'
                                  }}>
                                    ⏰ No Time Set
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '700' }}>{sub.maxMarks}m</span>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSingleSubject(sub.testId, sub.subjectName)}
                                  title="Remove this subject from exam"
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#ef4444',
                                    cursor: 'pointer',
                                    padding: '2px',
                                    display: 'flex'
                                  }}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Card Action Footer with Edit & Delete */}
                  <div style={{
                    padding: '10px 18px',
                    background: '#f8fafc',
                    borderTop: '1px solid #f1f5f9',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <button
                      type="button"
                      onClick={() => setExpandedExamKey(isExpanded ? null : exam.key)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: 'transparent',
                        border: 'none',
                        color: '#2563eb',
                        fontSize: '12.5px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      {isExpanded ? (
                        <>Hide Subjects <ChevronUp size={14} /></>
                      ) : (
                        <>View Subjects ({exam.subjects.length}) <ChevronDown size={14} /></>
                      )}
                    </button>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => {
                          if (typeof onOpenHallTickets === 'function') {
                            onOpenHallTickets({
                              examTitle: exam.title,
                              className: exam.className,
                              section: exam.section || 'ALL',
                              academicYear: academicYear
                            });
                          }
                        }}
                        title="Generate Hall Tickets for this exam section"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          background: '#eff6ff',
                          color: '#1e3a8a',
                          border: '1px solid #bfdbfe',
                          borderRadius: '6px',
                          padding: '5px 12px',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <Ticket size={12} /> Hall Tickets
                      </button>

                      <button
                        type="button"
                        onClick={() => handleOpenEditExam(exam)}
                        title="Edit exam timetable, dates, times, or subjects"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          background: '#ffffff',
                          color: '#1e3a8a',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          padding: '5px 12px',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <Edit2 size={12} /> Edit Timetable
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeleteExamGroup(exam)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          background: '#fef2f2',
                          color: '#dc2626',
                          border: '1px solid #fecaca',
                          borderRadius: '6px',
                          padding: '5px 10px',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        /* Teacher Assigned Tests View */
        teacherTests.length === 0 ? (
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            padding: '56px 24px',
            textAlign: 'center',
            border: '1.5px dashed #cbd5e1'
          }}>
            <BookOpen size={42} color="#94a3b8" style={{ marginBottom: '12px' }} />
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#334155' }}>
              {searchQuery ? `No teacher tests matching "${searchQuery}"` : 'No teacher-assigned tests found for this session'}
            </h4>
            <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#64748b', maxWidth: '540px', marginInline: 'auto' }}>
              When teachers create classroom tests, unit assessments, or quizzes from their teacher portal for {academicYear}, they will appear here separately from official school exams.
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '16px'
          }}>
            {teacherTests.map((test) => (
              <div
                key={test.testId}
                style={{
                  background: '#ffffff',
                  borderRadius: '14px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'box-shadow 0.15s ease'
                }}
              >
                {/* Header */}
                <div style={{ padding: '16px 18px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)',
                        border: '1px solid #99f6e4',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <BookOpen size={20} color="#0f766e" />
                      </div>
                      <div>
                        <h4 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.2px' }}>
                          {test.title}
                        </h4>
                        <div style={{ fontSize: '12px', color: '#0f766e', fontWeight: '700', marginTop: '2px' }}>
                          {test.subjectName || 'General Subject'}
                        </div>
                      </div>
                    </div>

                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: '800',
                      background: test.status === 'COMPLETED' ? '#f0fdf4' : '#eff6ff',
                      color: test.status === 'COMPLETED' ? '#16a34a' : '#0891b2',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      flexShrink: 0
                    }}>
                      {test.status === 'COMPLETED' ? '✓ Completed' : '● Scheduled'}
                    </span>
                  </div>
                </div>

                {/* Body - Visual Grid */}
                <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px'
                  }}>
                    <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Assigned By</div>
                      <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {getTeacherName(test.teacherId)}
                      </div>
                    </div>

                    <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Class & Sec</div>
                      <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {test.className} {test.section && test.section !== 'ALL' ? `(Sec ${test.section})` : ''}
                      </div>
                    </div>

                    <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Date</div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#334155', marginTop: '2px' }}>
                        {test.testDate || '—'}
                      </div>
                    </div>

                    <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Max Marks</div>
                      <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a', marginTop: '2px' }}>
                        {test.maxMarks} Marks
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div style={{
                  padding: '10px 18px',
                  background: '#f8fafc',
                  borderTop: '1px solid #f1f5f9',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{
                    background: '#f1f5f9',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '700',
                    color: '#475569'
                  }}>
                    {test.testType || 'Class Assessment'}
                  </span>

                  <button
                    type="button"
                    onClick={() => handleDeleteTeacherTest(test.testId, test.title)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      background: '#fef2f2',
                      color: '#dc2626',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      padding: '5px 10px',
                      fontSize: '12px',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* 4. Announce or Edit School Exam Modal */}
      {isModalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.55)',
          backdropFilter: 'blur(3px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '760px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)',
            border: '1px solid #cbd5e1',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh'
          }}>
            {/* Modal Header */}
            <div style={{
              background: '#1e3a8a',
              color: '#ffffff',
              padding: '16px 22px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800' }}>
                  {editingExamGroup ? `Edit School Exam: ${editingExamGroup.title}` : 'Announce School Exam'}
                </h3>
                <div style={{ fontSize: '12px', color: '#93c5fd', marginTop: '2px' }}>
                  {editingExamGroup
                    ? `Modify exam schedule and subjects for ${modalClass}.`
                    : 'Schedule an official examination for a class session.'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setIsModalOpen(false); setEditingExamGroup(null); }}
                style={{ background: 'transparent', border: 'none', color: '#ffffff', cursor: 'pointer', padding: '4px' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSaveExam} style={{ padding: '22px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>
              
              {/* Field 1: Exam Type */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                  Exam Type
                </label>
                <select
                  value={selectedExamTypeOption}
                  onChange={handleExamTypeSelect}
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    borderRadius: '8px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '13.5px',
                    fontWeight: '700',
                    color: '#0f172a',
                    background: '#ffffff',
                    cursor: 'pointer'
                  }}
                >
                  {availableExamTypes.map(et => (
                    <option key={et.examCode} value={et.examCode}>
                      {et.examCode} — {et.examName || et.examCode} ({et.defaultMaxMarks || 20} Marks)
                    </option>
                  ))}
                  <option value="__CUSTOM__">+ Custom Exam Name...</option>
                </select>

                {/* Custom Name Input if selected */}
                {selectedExamTypeOption === '__CUSTOM__' && (
                  <input
                    type="text"
                    placeholder="Enter exam name (e.g. Mid Term, Monthly Assessment)"
                    value={customExamTitle}
                    onChange={(e) => setCustomExamTitle(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      marginTop: '8px',
                      padding: '8px 12px',
                      borderRadius: '7px',
                      border: '1.5px solid #2563eb',
                      fontSize: '13px',
                      boxSizing: 'border-box'
                    }}
                  />
                )}
              </div>

              {/* Field 2 & 3: Class & Section */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                    Class
                  </label>
                  <select
                    value={modalClass}
                    onChange={(e) => {
                      setModalClass(e.target.value);
                      setModalSection('ALL');
                    }}
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      borderRadius: '8px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '13.5px',
                      fontWeight: '700',
                      color: '#0f172a',
                      background: '#ffffff',
                      cursor: 'pointer'
                    }}
                  >
                    {availableClasses.map(cls => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                    Section
                  </label>
                  <select
                    value={modalSection}
                    onChange={(e) => setModalSection(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      borderRadius: '8px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '13.5px',
                      fontWeight: '700',
                      color: '#0f172a',
                      background: '#ffffff',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="ALL">All Sections</option>
                    {modalSectionOptions.map(sec => (
                      <option key={sec} value={sec}>Section {sec}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Field 4: Subjects Checkboxes */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155' }}>
                    Subjects & Specific Marks ({selectedSubjects.length} selected)
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={handleSelectAllSubjects}
                      style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: '11.5px', fontWeight: '700', cursor: 'pointer', padding: 0 }}
                    >
                      Select All
                    </button>
                    <span style={{ color: '#cbd5e1' }}>|</span>
                    <button
                      type="button"
                      onClick={handleClearAllSubjects}
                      style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '11.5px', fontWeight: '700', cursor: 'pointer', padding: 0 }}
                    >
                      Clear All
                    </button>
                    <span style={{ color: '#cbd5e1' }}>|</span>
                    <button
                      type="button"
                      onClick={() => {
                        const resetMarks = {};
                        selectedSubjects.forEach(s => { resetMarks[s] = maxMarks; });
                        setCustomSubjectMarks(resetMarks);
                      }}
                      style={{ background: 'none', border: 'none', color: '#059669', fontSize: '11.5px', fontWeight: '700', cursor: 'pointer', padding: 0 }}
                      title="Reset all subject marks to default Maximum Marks"
                    >
                      Set all to {maxMarks}m
                    </button>
                  </div>
                </div>

                <div style={{
                  maxHeight: '190px',
                  overflowY: 'auto',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: '8px',
                  padding: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '5px',
                  background: '#f8fafc'
                }}>
                  {loadingSubjects ? (
                    <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center', padding: '12px' }}>Loading subjects...</div>
                  ) : (
                    (() => {
                      const allSubjectPool = allocatedSubjects.length > 0
                        ? [...new Set(allocatedSubjects.map(a => a.subjectName).filter(Boolean))]
                        : COMMON_SCHOOL_SUBJECTS;
                      const combinedList = [...new Set([...allSubjectPool, ...selectedSubjects])];

                      return combinedList.map(subj => {
                        const checked = selectedSubjects.includes(subj);
                        return (
                          <div
                            key={subj}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '5px 8px',
                              borderRadius: '6px',
                              background: checked ? '#ffffff' : '#f8fafc',
                              border: checked ? '1px solid #bfdbfe' : '1px solid #f1f5f9',
                              gap: '8px'
                            }}
                          >
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '12.5px',
                                fontWeight: checked ? '700' : '500',
                                color: checked ? '#1e3a8a' : '#475569',
                                cursor: 'pointer',
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => handleToggleSubject(subj)}
                                style={{ width: '15px', height: '15px', accentColor: '#1e3a8a', cursor: 'pointer', flexShrink: 0 }}
                              />
                              <span title={subj}>{subj}</span>
                            </label>

                            {checked && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                {editingMarksMap[subj] ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input
                                      type="number"
                                      min="1"
                                      max="1000"
                                      autoFocus
                                      value={customSubjectMarks[subj] !== undefined ? customSubjectMarks[subj] : maxMarks}
                                      onChange={(e) => handleSubjectMarkChange(subj, e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          setEditingMarksMap(prev => ({ ...prev, [subj]: false }));
                                        }
                                      }}
                                      style={{
                                        width: '56px',
                                        padding: '3px 6px',
                                        borderRadius: '5px',
                                        border: '2px solid #2563eb',
                                        fontSize: '12px',
                                        fontWeight: '800',
                                        textAlign: 'center',
                                        color: '#0f172a',
                                        background: '#ffffff'
                                      }}
                                      title={`Set specific maximum marks for ${subj}`}
                                    />
                                    <span style={{ fontSize: '11px', color: '#64748b' }}>m</span>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingMarksMap(prev => ({ ...prev, [subj]: false }));
                                      }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                        padding: '3px 8px',
                                        borderRadius: '5px',
                                        background: '#1e3a8a',
                                        color: '#ffffff',
                                        border: 'none',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        cursor: 'pointer'
                                      }}
                                      title="Save marks for this subject"
                                    >
                                      <Check size={12} /> Set
                                    </button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{
                                      fontSize: '11.5px',
                                      fontWeight: '700',
                                      padding: '2px 8px',
                                      borderRadius: '6px',
                                      background: (customSubjectMarks[subj] !== undefined && customSubjectMarks[subj] !== '' && parseInt(customSubjectMarks[subj], 10) !== parseInt(baseMaxMarks, 10)) ? '#eff6ff' : '#f1f5f9',
                                      color: (customSubjectMarks[subj] !== undefined && customSubjectMarks[subj] !== '' && parseInt(customSubjectMarks[subj], 10) !== parseInt(baseMaxMarks, 10)) ? '#1d4ed8' : '#475569',
                                      border: (customSubjectMarks[subj] !== undefined && customSubjectMarks[subj] !== '' && parseInt(customSubjectMarks[subj], 10) !== parseInt(baseMaxMarks, 10)) ? '1px solid #bfdbfe' : '1px solid #cbd5e1'
                                    }}>
                                      {(customSubjectMarks[subj] !== undefined && customSubjectMarks[subj] !== '' ? parseInt(customSubjectMarks[subj], 10) : (parseInt(baseMaxMarks, 10) || 0)) + (componentType !== 'NONE' ? (parseInt(componentMaxMarks, 10) || 0) : 0)} Marks
                                    </span>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingMarksMap(prev => ({ ...prev, [subj]: true }));
                                      }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '3px 8px',
                                        borderRadius: '5px',
                                        background: '#ffffff',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        color: '#2563eb',
                                        cursor: 'pointer',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                                      }}
                                      title="Click to edit maximum marks for this subject"
                                    >
                                      <Edit2 size={11} /> Edit
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()
                  )}
                </div>

                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>💡 All subjects use <strong>{maxMarks} marks</strong> by default. Click <strong>"Edit"</strong> on any subject if it has specific marks.</span>
                </div>

                {/* Inline custom subject add */}
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                  <input
                    type="text"
                    placeholder="+ Add other subject..."
                    value={customSubjectInput}
                    onChange={(e) => setCustomSubjectInput(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '12px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomSubject}
                    style={{
                      background: '#f1f5f9',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#334155',
                      cursor: 'pointer'
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Marks & Additional Component Configuration Card */}
              <div style={{
                background: '#f8fafc',
                border: '1.5px solid #cbd5e1',
                borderRadius: '10px',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>⚙️</span> Marks & Additional Component Configuration
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: '#2563eb', background: '#eff6ff', padding: '2px 8px', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
                    Applies to All Selected Subjects
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  {/* Base Maximum Marks */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                      Base Maximum Marks
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={baseMaxMarks}
                      onChange={(e) => {
                        const val = e.target.value;
                        setBaseMaxMarks(val);
                        const base = parseInt(val, 10) || 0;
                        const comp = componentType !== 'NONE' ? (parseInt(componentMaxMarks, 10) || 0) : 0;
                        setMaxMarks(base + comp);
                        setCustomSubjectMarks({});
                      }}
                      required
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1.5px solid #cbd5e1',
                        fontSize: '13px',
                        fontWeight: '700',
                        color: '#0f172a',
                        boxSizing: 'border-box'
                      }}
                      placeholder="e.g. 40 or 80"
                    />
                    <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                      Written / Theory marks
                    </span>
                  </div>

                  {/* Additional Component Type */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                      Additional Component
                    </label>
                    <select
                      value={componentType}
                      onChange={(e) => {
                        const val = e.target.value;
                        setComponentType(val);
                        let comp = parseInt(componentMaxMarks, 10) || 0;
                        if (val === 'ORAL' && comp === 0) {
                          comp = 10;
                          setComponentMaxMarks(10);
                        } else if (val === 'INTERNAL' && comp === 0) {
                          comp = 20;
                          setComponentMaxMarks(20);
                        } else if (val === 'NONE') {
                          comp = 0;
                          setComponentMaxMarks(0);
                        }
                        const base = parseInt(baseMaxMarks, 10) || 0;
                        setMaxMarks(base + (val !== 'NONE' ? comp : 0));
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1.5px solid #cbd5e1',
                        fontSize: '13px',
                        fontWeight: '700',
                        color: '#0f172a',
                        background: '#ffffff',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="NONE">None (Single Marks)</option>
                      <option value="ORAL">Oral</option>
                      <option value="INTERNAL">Internal (IA)</option>
                    </select>
                    <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                      Added on top of base
                    </span>
                  </div>

                  {/* Component Maximum Marks */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: componentType === 'NONE' ? '#94a3b8' : '#334155', marginBottom: '6px' }}>
                      {componentType === 'ORAL' ? 'Oral Max Marks' : componentType === 'INTERNAL' ? 'Internal Max Marks' : 'Component Max Marks'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="500"
                      disabled={componentType === 'NONE'}
                      value={componentType === 'NONE' ? 0 : componentMaxMarks}
                      onChange={(e) => {
                        const val = e.target.value;
                        setComponentMaxMarks(val);
                        const comp = parseInt(val, 10) || 0;
                        const base = parseInt(baseMaxMarks, 10) || 0;
                        setMaxMarks(base + (componentType !== 'NONE' ? comp : 0));
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1.5px solid #cbd5e1',
                        fontSize: '13px',
                        fontWeight: '700',
                        color: componentType === 'NONE' ? '#94a3b8' : '#0f172a',
                        background: componentType === 'NONE' ? '#f1f5f9' : '#ffffff',
                        boxSizing: 'border-box'
                      }}
                      placeholder="e.g. 10 or 20"
                    />
                    <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                      Additional (NOT deducted)
                    </span>
                  </div>
                </div>

                {/* Live Preview Banner */}
                <div style={{
                  background: componentType === 'NONE' ? '#f1f5f9' : componentType === 'ORAL' ? '#f0fdf4' : '#eff6ff',
                  border: `1.5px solid ${componentType === 'NONE' ? '#cbd5e1' : componentType === 'ORAL' ? '#86efac' : '#93c5fd'}`,
                  borderRadius: '8px',
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '18px' }}>
                    {componentType === 'NONE' ? '📄' : componentType === 'ORAL' ? '🗣️' : '📝'}
                  </span>
                  <div>
                    <div style={{ fontSize: '13.5px', fontWeight: '800', color: componentType === 'NONE' ? '#334155' : componentType === 'ORAL' ? '#166534' : '#1e40af' }}>
                      {componentType === 'ORAL' && (
                        `Written: ${baseMaxMarks} + Oral: ${componentMaxMarks} = Total: ${totalCalculatedMax}`
                      )}
                      {componentType === 'INTERNAL' && (
                        `Theory: ${baseMaxMarks} + Internal: ${componentMaxMarks} = Total: ${totalCalculatedMax}`
                      )}
                      {componentType === 'NONE' && (
                        `Marks: ${baseMaxMarks} = Total: ${baseMaxMarks}`
                      )}
                    </div>
                    <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>
                      {componentType !== 'NONE'
                        ? 'Component marks are ADDITIONAL and will NOT be deducted from Base Maximum Marks.'
                        : 'Standard single-component marks for all selected subjects.'}
                    </div>
                  </div>
                </div>

                {/* Multi-Class Propagate Option */}
                <div style={{
                  background: '#f0fdf4',
                  border: '1.5px solid #86efac',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <input
                    type="checkbox"
                    id="applyToOtherClassesCb"
                    checked={applyToOtherClasses}
                    onChange={(e) => setApplyToOtherClasses(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#16a34a' }}
                  />
                  <label htmlFor="applyToOtherClassesCb" style={{ fontSize: '12.5px', fontWeight: '700', color: '#166534', cursor: 'pointer' }}>
                    Also update {selectedExamTypeOption === '__CUSTOM__' ? (customExamTitle || 'this exam') : (selectedExamTypeOption || 'this exam')} marks ({totalCalculatedMax} Marks) for all other classes
                  </label>
                </div>
              </div>

              {/* Field 5: Exam Date */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                  Exam Date
                </label>
                <input
                  type="date"
                  value={testDate}
                  onChange={(e) => setTestDate(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '13px',
                    color: '#0f172a',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Field 7 & 8: Optional Start Time & End Time */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                    Start Time <span style={{ fontSize: '11px', fontWeight: '400', color: '#64748b' }}>(Optional)</span>
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '13px',
                      color: '#0f172a',
                      boxSizing: 'border-box'
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                    e.g. 09:00 AM (leave blank if unscheduled)
                  </span>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                    End Time <span style={{ fontSize: '11px', fontWeight: '400', color: '#64748b' }}>(Optional)</span>
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '13px',
                      color: '#0f172a',
                      boxSizing: 'border-box'
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                    e.g. 09:30 AM
                  </span>
                </div>
              </div>

              {/* Subject-Wise Timetable & Schedule Section */}
              {selectedSubjects.length > 0 && (
                <div style={{
                  background: '#f8fafc',
                  borderRadius: '10px',
                  border: '1.5px solid #cbd5e1',
                  padding: '14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>📅</span> Subject Examination Timetable ({selectedSubjects.length} Subjects)
                      </div>
                      <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>
                        Set a different date and start/end time for each subject paper.
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        onClick={handleAutoStaggerDates}
                        title="Assign consecutive dates to each subject (skipping Sundays) starting from the base date"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: '#eff6ff',
                          color: '#1d4ed8',
                          border: '1px solid #bfdbfe',
                          borderRadius: '6px',
                          padding: '5px 10px',
                          fontSize: '11.5px',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        ⚡ +1 Day Each (Consecutive)
                      </button>

                      <button
                        type="button"
                        onClick={handleApplyTimeToAll}
                        title="Apply first subject's start & end time to all subjects"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: '#ffffff',
                          color: '#334155',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          padding: '5px 10px',
                          fontSize: '11.5px',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        ⏰ Apply Time to All
                      </button>
                    </div>
                  </div>

                  {/* Timetable Table */}
                  <div style={{ overflowX: 'auto', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#ffffff', maxHeight: '240px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: '#f1f5f9', color: '#334155', textAlign: 'left', fontWeight: '700', position: 'sticky', top: 0, zIndex: 2 }}>
                          <th style={{ padding: '8px 12px', borderBottom: '1px solid #cbd5e1' }}>Subject</th>
                          <th style={{ padding: '8px 12px', borderBottom: '1px solid #cbd5e1', minWidth: '145px' }}>Date</th>
                          <th style={{ padding: '8px 12px', borderBottom: '1px solid #cbd5e1', minWidth: '115px' }}>Start Time</th>
                          <th style={{ padding: '8px 12px', borderBottom: '1px solid #cbd5e1', minWidth: '115px' }}>End Time</th>
                          <th style={{ padding: '8px 12px', borderBottom: '1px solid #cbd5e1', width: '80px' }}>Max Marks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedSubjects.map((subj, idx) => {
                          const subjDate = customSubjectDates[subj] || testDate || '';
                          const subjStart = customSubjectStartTimes[subj] !== undefined ? customSubjectStartTimes[subj] : (startTime || '');
                          const subjEnd = customSubjectEndTimes[subj] !== undefined ? customSubjectEndTimes[subj] : (endTime || '');
                          const subjMark = customSubjectMarks[subj] !== undefined ? customSubjectMarks[subj] : maxMarks;

                          return (
                            <tr key={subj} style={{ borderBottom: idx === selectedSubjects.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                              <td style={{ padding: '8px 12px', fontWeight: '700', color: '#1e293b' }}>
                                {subj}
                              </td>
                              <td style={{ padding: '6px 10px' }}>
                                <input
                                  type="date"
                                  value={subjDate}
                                  onChange={(e) => setCustomSubjectDates(prev => ({ ...prev, [subj]: e.target.value }))}
                                  required
                                  style={{
                                    width: '100%',
                                    padding: '5px 8px',
                                    borderRadius: '5px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '12px',
                                    color: '#0f172a',
                                    background: '#ffffff',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              </td>
                              <td style={{ padding: '6px 10px' }}>
                                <input
                                  type="time"
                                  value={subjStart}
                                  onChange={(e) => setCustomSubjectStartTimes(prev => ({ ...prev, [subj]: e.target.value }))}
                                  style={{
                                    width: '100%',
                                    padding: '5px 8px',
                                    borderRadius: '5px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '12px',
                                    color: '#0f172a',
                                    background: '#ffffff',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              </td>
                              <td style={{ padding: '6px 10px' }}>
                                <input
                                  type="time"
                                  value={subjEnd}
                                  onChange={(e) => setCustomSubjectEndTimes(prev => ({ ...prev, [subj]: e.target.value }))}
                                  style={{
                                    width: '100%',
                                    padding: '5px 8px',
                                    borderRadius: '5px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '12px',
                                    color: '#0f172a',
                                    background: '#ffffff',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              </td>
                              <td style={{ padding: '6px 10px' }}>
                                <input
                                  type="number"
                                  min="1"
                                  max="1000"
                                  value={subjMark}
                                  onChange={(e) => handleSubjectMarkChange(subj, e.target.value)}
                                  style={{
                                    width: '65px',
                                    padding: '5px 6px',
                                    borderRadius: '5px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    textAlign: 'center',
                                    color: '#0f172a',
                                    background: '#ffffff',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Modal Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px', paddingTop: '14px', borderTop: '1px solid #f1f5f9' }}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    padding: '9px 18px',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    background: submitting ? '#94a3b8' : '#1e3a8a',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '9px 22px',
                    fontSize: '13.5px',
                    fontWeight: '700',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    boxShadow: '0 2px 4px rgba(30,58,138,0.25)'
                  }}
                >
                  {submitting ? (editingExamGroup ? 'Saving...' : 'Announcing...') : (editingExamGroup ? 'Save Changes' : 'Announce Exam')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamSetup;
